/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Image } from '../../Images';
import { withMyTheme, DESKTOP_TITLE_FONT_SIZE, MOBILE_TITLE_FONT_SIZE } from '../../theme/theme';
import { mobileCss } from '../../theme/isMobile';
import { MyButton } from '../../components/button/MyButton';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';

export const ABOUT_ME_ID = 'about-me';

const fadeInAnimation = css`
    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(10vh);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

const AboutMeContainerStyle = withMyTheme((theme) => css`
    background-color: ${theme.palette.background.default};
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8vh 0;
    gap: 5vh;

    ${mobileCss(`
        padding: 6vh 0;
        gap: 4vh;
    `)}
`);

const ContentContainerStyle = (isVisible: boolean) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2vh;
    max-width: 960px;
    text-align: center;
    padding: 0 5vw;
    opacity: 0;
    transform: translateY(10vh);

    ${isVisible && css`
        animation: fadeIn 1s ease-out forwards;
        ${fadeInAnimation}
    `}
`;

const ButtonStyle = withMyTheme((theme) => css`
    align-self: center;
    margin-top: -1vh;
    &.MuiButton-contained {
        background-color: ${theme.palette.primary.dark};
    }

    ${mobileCss(`
        margin-top: 1vh;
    `)}
`);

const AboutMeTitleStyle = withMyTheme((theme) => css`
    font-weight: 600;
    color: ${theme.palette.text.primary};
    font-family: ${theme.typography.h1.fontFamily};
    margin: 0;
    font-size: ${DESKTOP_TITLE_FONT_SIZE};

    ${mobileCss(`
        font-size: ${MOBILE_TITLE_FONT_SIZE};
    `)}
`);

const AboutMeDescriptionStyle = withMyTheme((theme) => css`
    color: ${theme.palette.text.primary};
    font-size: 1.15rem;
    line-height: 1.8;
    white-space: pre-line;
    font-family: ${theme.typography.body1.fontFamily};

    ${mobileCss(`
        font-size: 1rem;
        line-height: 1.6;
    `)}
`);

const ImageStripStyle = (isVisible: boolean) => css`
    display: flex;
    width: 100%;
    box-sizing: border-box;
    gap: 6px;
    opacity: 0;
    transform: translateY(10vh);
    animation-delay: 0.2s;

    ${isVisible && css`
        animation: fadeIn 1s ease-out forwards;
        ${fadeInAnimation}
    `}

    ${mobileCss(`
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        -webkit-overflow-scrolling: touch;
        padding: 0 12vw;
        scrollbar-width: none;
        -ms-overflow-style: none;

        &::-webkit-scrollbar {
            display: none;
        }
    `)}
`;

const ImageCellStyle = (grow: number) => css`
    flex: ${grow} ${grow} 0;
    height: 62vh;
    overflow: hidden;

    ${mobileCss(`
        flex: 0 0 76vw;
        height: 55vh;
        scroll-snap-align: center;
        scroll-snap-stop: always;
    `)}
`;

const AboutMeImageStyle = css`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.5s ease;

    &:hover {
        transform: scale(1.04);
    }
`;

const ABOUT_ME_IMAGES = [
    { src: Image.ABOUT_ME_1, grow: 1, width: 1334, height: 2000 },
    { src: Image.ABOUT_ME_2, grow: 1, width: 1334, height: 2000 },
    { src: Image.ABOUT_ME_3, grow: 1, width: 1334, height: 2000 },
    { src: Image.ABOUT_ME_4, grow: 1, width: 1334, height: 2000 },
    { src: Image.ABOUT_ME_5, grow: 1, width: 1334, height: 2000 },
];

export const AboutMe = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.unobserve(entry.target);
                }
            },
            {
                threshold: 0.2, // Trigger when 20% of the element is visible
                rootMargin: '50px' // Start animation slightly before the element comes into view
            }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    return (
        <div css={AboutMeContainerStyle} ref={sectionRef} id={ABOUT_ME_ID}>
            <div css={ContentContainerStyle(isVisible)}>
                <h2 css={AboutMeTitleStyle}>
                    {t('aboutMe.title')}
                </h2>
                <p css={AboutMeDescriptionStyle}>
                    {t('aboutMe.shortDescription')}
                </p>
                <MyButton
                    text="CZYTAJ WIĘCEJ"
                    onClick={() => navigate(`/poznajmy-sie`)}
                    variant="contained"
                    additionalCss={ButtonStyle}
                />
            </div>

            <div css={ImageStripStyle(isVisible)}>
                {ABOUT_ME_IMAGES.map(({ src, grow, width, height }, index) => (
                    <div key={index} css={ImageCellStyle(grow)}>
                        <img
                            src={src}
                            alt={`${t('aboutMe.title')} - zdjęcie ${index + 1}`}
                            width={width}
                            height={height}
                            css={AboutMeImageStyle}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};
